import { format, formatDistanceToNow } from 'date-fns';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function getUserTimezone(): string {
	try {
		return Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
	} catch {
		return 'UTC';
	}
}

export function isUTCDate(dateString: string): boolean {
	if (typeof dateString !== 'string') return false;
	const isoUTCRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
	if (!isoUTCRegex.test(dateString)) return false;
	const date = new Date(dateString);
	return !isNaN(date.getTime());
}

/**
 * Whether a timestamp has already elapsed. Closing dates are stored at the last instant of the
 * chosen day, so a job closing today is still open.
 */
export function isDateInPast(dateInput?: string | number | Date | null): boolean {
	if (dateInput == null) return false;
	const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
	if (Number.isNaN(date.getTime())) return false;

	return date.getTime() < Date.now();
}

export function formatLongDate(dateInput?: string | number | Date | null): string | undefined {
	if (dateInput == null) return;
	const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
	if (Number.isNaN(date.getTime())) return;

	return date.toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});
}

export function formatDate(dateString: string): string {
	const date = new Date(dateString);

	const month = MONTHS[date.getMonth()];
	const day = date.getDate();
	const year = date.getFullYear();

	let hours = date.getHours();
	const minutes = date.getMinutes();
	const ampm = hours >= 12 ? 'PM' : 'AM';

	// Convert to 12-hour format
	hours = hours % 12;
	hours = hours ? hours : 12; // Handle midnight (0 hours)

	// Pad minutes with leading zero if needed
	const minutesStr = minutes < 10 ? `0${minutes}` : minutes;

	return `${month} ${day}, ${year} at ${hours}:${minutesStr} ${ampm}`;
}

export function getAmPmTimeFromUTC(utcTimeStamp: string): string | undefined {
	if (!utcTimeStamp) return;
	const date = new Date(utcTimeStamp);

	const timeString = date.toLocaleTimeString('en-US', {
		timeZone: getUserTimezone(),
		hour: '2-digit',
		minute: '2-digit',
		hour12: true,
	});

	// Remove leading zero from hour and ensure single space before AM/PM
	return timeString.replace(/^0/, '');
}

export function isSameDayTime(utcTimeStamp1?: string, utcTimeStamp2?: string): boolean | undefined {
	if (!utcTimeStamp1 || !utcTimeStamp2) return;
	const date1 = new Date(utcTimeStamp1).setSeconds(0, 0);
	const date2 = new Date(utcTimeStamp2).setSeconds(0, 0);

	return date1 === date2;
}

export function isTodayTime(utcTimestamp: string): boolean | undefined {
	if (!utcTimestamp) return;

	const dateInUTC = new Date(utcTimestamp).setHours(0, 0, 0, 0);
	const todayDateInUTC = new Date().setHours(0, 0, 0, 0);

	return dateInUTC === todayDateInUTC;
}

export function getMonthDayFromUTC(utcTimeStamp: string): string | undefined {
	if (!utcTimeStamp) return;

	const date = new Date(utcTimeStamp);

	return date.toLocaleDateString('en-US', {
		timeZone: getUserTimezone(),
		month: 'short',
		day: '2-digit',
	});
}

export function getDayMonthFromUTC(utcTimeStamp: string): string | undefined {
	if (!utcTimeStamp) return;

	const date = new Date(utcTimeStamp);

	return date.toLocaleDateString('en-GB', {
		timeZone: getUserTimezone(),
		day: 'numeric',
		month: 'short',
	});
}

export function getDayMonthYearFromWithTimeUTC(
	utcTimeStamp: string,
	excludeCurrentYearChecks?: boolean
): string | undefined {
	try {
		if (!utcTimeStamp) return;

		const date = new Date(utcTimeStamp);
		const currentYear = new Date().getFullYear();
		const dateYear = date.getFullYear();
		const isCurrentYear = currentYear === dateYear;

		const formatOptions: Intl.DateTimeFormatOptions = {
			timeZone: getUserTimezone(),
			day: 'numeric',
			month: 'short',
			...(isCurrentYear && !excludeCurrentYearChecks ? {} : { year: 'numeric' }),
		};

		const formattedDate = date.toLocaleDateString('en-GB', formatOptions);

		let hours = date.getHours();
		const minutes = date.getMinutes();
		const ampm = hours >= 12 ? 'PM' : 'AM';

		// Convert to 12-hour format
		hours = hours % 12;
		hours = hours || 12; // Handle midnight (0 hours)

		// Pad minutes with leading zero if needed
		const minutesStr = minutes < 10 ? `0${minutes}` : minutes;

		return `${formattedDate}, ${hours}:${minutesStr} ${ampm}`;
	} catch (err) {
		console.error('error parsing date', err);
		return utcTimeStamp;
	}
}

export function getDayMonthYearFromUTC(
	utcTimeStamp: string,
	excludeDay: boolean = false
): string | undefined {
	if (!utcTimeStamp) return;
	try {
		const date = new Date(utcTimeStamp);

		return date.toLocaleDateString('en-GB', {
			timeZone: getUserTimezone(),
			day: excludeDay ? undefined : '2-digit',
			month: 'short',
			year: 'numeric',
		});
	} catch (error) {
		console.log('error parsing date', error);
		return;
	}
}

export function getMinutesDiffFromUTC(utcTimestamp: string): number | undefined {
	if (!utcTimestamp) return;
	const prevTime = new Date(utcTimestamp);
	const currentTime = new Date();

	// Check if both timestamps are on the same day
	if (
		currentTime.getFullYear() !== prevTime.getFullYear() ||
		currentTime.getMonth() !== prevTime.getMonth() ||
		currentTime.getDate() !== prevTime.getDate()
	) {
		return undefined;
	}

	// Calculate the time difference in milliseconds
	const timeDifference = currentTime.getTime() - prevTime.getTime();

	// Convert milliseconds to minutes
	return Math.floor(timeDifference / (1000 * 60));
}

export function getMonthNameFromNumber(monthNumber: number): string {
	return MONTHS[monthNumber - 1] ?? '';
}

interface DateObj {
	year: number;
	month: number;
}

export function getMonthsDifference(startDate: DateObj | null, endDate: DateObj | null): number {
	if (!startDate) return 0;

	const startYear: number = startDate.year;
	const startMonth: number = startDate.month;

	// If endDate is not provided, default to the current month and year
	const currentDate: Date = new Date();
	const endYear: number = endDate?.year ?? currentDate.getFullYear();
	const endMonth: number = endDate?.month ?? currentDate.getMonth() + 1;

	return (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
}

export function getMonthYearText(
	startMonth?: number | null,
	startYear?: number | null,
	endMonth?: number | null,
	endYear?: number | null,
	showPresentText?: boolean
): string {
	const startMonthYear =
		startMonth || startYear
			? `${startMonth ? getMonthNameFromNumber(startMonth) : ''} ${startYear ?? ''}`.trim()
			: '';
	const endMonthYear =
		endMonth || endYear
			? `${endMonth ? getMonthNameFromNumber(endMonth) : ''} ${endYear ?? ''}`.trim()
			: '';

	const endMonthYearText = endMonthYear
		? ` - ${endMonthYear}`
		: `${showPresentText ? ' - Present' : ''}`;

	return startMonthYear ? `${startMonthYear}${endMonthYearText}` : '';
}

export function getTotalYearsMonthText(months: number): string {
	if (!months) return '';

	const monthsModular = months % 12;
	const years = Math.floor(months / 12);

	const yearsText = `${years} ${years > 1 ? 'yrs' : 'yr'}`;
	const monthsText = `${monthsModular} ${monthsModular > 1 ? 'mos' : 'mo'}`;

	if (months < 12) return monthsText;
	if (!monthsModular) return yearsText;
	return `${yearsText} ${monthsText}`;
}

export function getTimeDifferenceWithPresent(utcTimestamp: string): number {
	if (!utcTimestamp) return 0;

	const date = new Date(utcTimestamp);
	const now = new Date();

	const timeDifference = date.getTime() - now.getTime();

	return timeDifference;
}

export function getMessageTimeFormatted(
	utcTimestamp: string = '',
	shortTime: boolean = true
): string {
	if (!utcTimestamp) return '';

	const date = new Date(utcTimestamp);
	const isSameDay = isTodayTime(utcTimestamp);
	const output = isSameDay
		? formatDistanceToNow(date, { addSuffix: true })
		: format(date, 'MMM dd, hh:mm a');

	if (!shortTime) return output;

	return output.replaceAll('second', 'sec').replaceAll('minute', 'min').replaceAll('hour', 'hr');
}
